import * as yup from 'yup';
import { requiredAmount, requiredCount } from 'pages/PosSettings/numberRules';

export const PROMOTION_TYPES = [
  { label: 'Fixed Discount', value: 'FIXED_DISCOUNT' },
  { label: 'Buy X Get Y', value: 'BUY_X_GET_Y' },
  { label: 'Discount', value: 'FREE_ITEM' },
  { label: 'Free Delivery', value: 'FREE_DELIVERY' },
  { label: 'Price by Quantity', value: 'PRICE_BY_QUANTITY' },
];

export const ACTION_SCOPES = {
  order: 'ORDER',
  item: 'ITEM',
};

export const SCOPE_OPTIONS = [
  {
    value: ACTION_SCOPES.order,
    label: 'Order level — items counted together across the order',
  },
  {
    value: ACTION_SCOPES.item,
    label: 'Item level — each chosen product counted on its own',
  },
];

/**
 * Switching type asks for a different discount, so the numbers entered for the
 * previous one are cleared, and the level has to be chosen again. A form
 * opened from a menu item or category passes its fixed item level instead.
 * Targets are kept: they say what the promotion is about, not how it
 * discounts.
 */
export const applyPromotionType = (setValue, type, lockedScope) => {
  const options = { shouldDirty: true, shouldValidate: true };
  setValue('type', type, options);
  setValue('actionScope', lockedScope || '', options);
  setValue('actionDiscountMode', 'free', options);
  setValue('actionValue', '', options);
  setValue('actionBuyX', '', options);
  setValue('actionGetY', '', options);
};

const PERCENT_MODE_TYPES = ['BUY_X_GET_Y', 'FREE_ITEM'];

// Which fields each type actually asks for. A field the form never showed for
// the chosen type is left out, so a value typed under an earlier type cannot
// travel with the one being saved.
const VALUE_TYPES = [
  'PERCENTAGE_DISCOUNT',
  'FIXED_DISCOUNT',
  'PRICE_BY_QUANTITY',
];
const BUY_X_TYPES = ['BUY_X_GET_Y', 'PRICE_BY_QUANTITY'];
const GET_Y_TYPES = ['BUY_X_GET_Y'];

const actionValueOf = (values) => {
  if (PERCENT_MODE_TYPES.includes(values.type)) {
    return values.actionDiscountMode === 'free'
      ? 100
      : Number(values.actionValue) || 0;
  }
  return VALUE_TYPES.includes(values.type) ? Number(values.actionValue) || 0 : 0;
};

export const buildPromotionAction = (values) => {
  const targetItemUuids = values.actionTargetItemUuids || [];
  const targetCategoryUuids = values.actionTargetCategoryUuids || [];

  const action = {
    type: values.type,
    value: actionValueOf(values),
    scope: values.actionScope,
  };

  if (BUY_X_TYPES.includes(values.type))
    action.buyX = Number(values.actionBuyX);
  if (GET_Y_TYPES.includes(values.type))
    action.getY = Number(values.actionGetY);
  if (targetItemUuids.length > 0) action.targetItemUuids = targetItemUuids;
  if (targetCategoryUuids.length > 0)
    action.targetCategoryUuids = targetCategoryUuids;

  return action;
};

export const actionToFormValues = (action = {}) => ({
  actionScope: action.scope || '',
  actionDiscountMode:
    action.value != null && action.value > 0 && action.value < 100
      ? 'discount'
      : 'free',
  actionValue: action.value ?? '',
  actionBuyX: action.buyX ?? '',
  actionGetY: action.getY ?? '',
  actionTargetItemUuids: action.targetItemUuids || [],
  actionTargetCategoryUuids: action.targetCategoryUuids || [],
});

export const actionDefaultValues = {
  actionScope: '',
  actionDiscountMode: 'free',
  actionValue: '',
  actionBuyX: '',
  actionGetY: '',
  actionTargetItemUuids: [],
  actionTargetCategoryUuids: [],
};

/** Shared between the promotion form and the coupon form, which both write an action. */
export const actionSchemaFields = {
  actionDiscountMode: yup.string().nullable(),
  // Each field is only filled in for the types that render it, so the rules
  // apply per type rather than to whatever the last type left behind.
  actionValue: yup
    .mixed()
    .when(['type', 'actionDiscountMode'], {
      is: (type, mode) =>
        type === 'PERCENTAGE_DISCOUNT' ||
        (PERCENT_MODE_TYPES.includes(type) && mode === 'discount'),
      then: requiredAmount({ min: 1, max: 100 }),
    })
    .when('type', {
      is: (type) => type === 'FIXED_DISCOUNT' || type === 'PRICE_BY_QUANTITY',
      then: requiredAmount({ min: 0 }),
    }),
  actionBuyX: yup.mixed().when('type', {
    is: (type) => BUY_X_TYPES.includes(type),
    then: requiredCount({ min: 1 }),
  }),
  actionGetY: yup.mixed().when('type', {
    is: (type) => GET_Y_TYPES.includes(type),
    then: requiredCount({ min: 1 }),
  }),
  actionScope: yup
    .string()
    .required('field_is_required_')
    .oneOf(Object.values(ACTION_SCOPES)),
  // An item-level action the engine has no target for is skipped entirely, so
  // it can't be saved without one.
  actionTargetItemUuids: yup
    .array()
    .of(yup.string())
    .nullable()
    .test(
      'targets-required',
      'Select at least one product or category',
      function (value) {
        const { actionScope, actionTargetCategoryUuids } = this.parent;
        if (actionScope !== ACTION_SCOPES.item) return true;
        return !!(value?.length || actionTargetCategoryUuids?.length);
      }
    ),
  actionTargetCategoryUuids: yup.array().of(yup.string()).nullable(),
};

/** Renders what a saved action does, for list views. */
export const describeAction = (type, action = {}) => {
  const value = action.value ?? 0;
  switch (type) {
    case 'PERCENTAGE_DISCOUNT':
      return `${value}%`;
    case 'FIXED_DISCOUNT':
      return `$${value}`;
    case 'FREE_DELIVERY':
      return '—';
    case 'BUY_X_GET_Y':
      return `Buy ${action.buyX ?? 0} get ${action.getY ?? 0}${
        value > 0 && value < 100 ? ` at ${value}% off` : ' free'
      }`;
    case 'FREE_ITEM':
      return value > 0 && value < 100 ? `${value}% off` : 'Free';
    case 'PRICE_BY_QUANTITY':
      return `${action.buyX ?? 0} for $${value}`;
    default:
      return '—';
  }
};
